// Theme presets
export const THEME_PRESETS = {
  dark: {
    bg: '#0a0a0f',
    bgCard: '#12121a',
    bgElevated: '#1a1a24',
    bgHover: '#22222e',
    bgActive: '#2a2a38',
    border: '#1e1e2a',
    borderLight: '#2a2a3a',
    text: '#f1f5f9',
    textSecondary: '#94a3b8',
    textMuted: '#64748b',
    textDim: '#475569',
    shadow: 'rgba(0,0,0,0.4)',
    shadowHeavy: 'rgba(0,0,0,0.6)',
  },
  light: {
    bg: '#f8fafc',
    bgCard: '#ffffff',
    bgElevated: '#f1f5f9',
    bgHover: '#e2e8f0',
    bgActive: '#cbd5e1',
    border: '#e2e8f0',
    borderLight: '#cbd5e1',
    text: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    textDim: '#94a3b8',
    shadow: 'rgba(0,0,0,0.1)',
    shadowHeavy: 'rgba(0,0,0,0.2)',
  },
  oled: {
    bg: '#000000',
    bgCard: '#0a0a0a',
    bgElevated: '#111111',
    bgHover: '#1a1a1a',
    bgActive: '#222222',
    border: '#1a1a1a',
    borderLight: '#222222',
    text: '#ffffff',
    textSecondary: '#a0a0a0',
    textMuted: '#707070',
    textDim: '#505050',
    shadow: 'rgba(0,0,0,0.8)',
    shadowHeavy: 'rgba(0,0,0,0.95)',
  },
};

// Get saved theme settings from localStorage
export const getSavedThemeMode = () => localStorage.getItem('themeMode') || 'dark';
export const getSavedAccentColor = () => localStorage.getItem('accentColor') || '#3b82f6';

// Build theme from preset and accent color
export const buildTheme = (mode = 'dark', accentColor = '#3b82f6') => {
  const preset = THEME_PRESETS[mode] || THEME_PRESETS.dark;
  return {
    omega: '#f97316',
    omegaLight: 'rgba(249,115,22,0.08)',
    omegaMedium: 'rgba(249,115,22,0.15)',
    omegaBorder: 'rgba(249,115,22,0.25)',
    omegaGlow: 'rgba(249,115,22,0.4)',
    supe: '#a855f7',
    supeLight: 'rgba(168,85,247,0.08)',
    supeMedium: 'rgba(168,85,247,0.15)',
    supeBorder: 'rgba(168,85,247,0.25)',
    supeGlow: 'rgba(168,85,247,0.4)',
    accent: accentColor,
    success: '#10b981',
    successLight: 'rgba(16,185,129,0.08)',
    successGlow: 'rgba(16,185,129,0.4)',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    infoLight: 'rgba(59,130,246,0.08)',
    pink: '#ec4899',
    // Enhanced design tokens for modern UI
    glass: mode === 'dark' ? 'rgba(18, 18, 26, 0.75)' : 'rgba(255, 255, 255, 0.85)',
    glassLight: mode === 'dark' ? 'rgba(26, 26, 36, 0.6)' : 'rgba(255, 255, 255, 0.7)',
    glassBorder: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    cardGlow: mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
    cardGlowHover: mode === 'dark' ? '0 16px 48px rgba(0, 0, 0, 0.5)' : '0 16px 48px rgba(0, 0, 0, 0.18)',
    modalShadow: '0 24px 80px rgba(0, 0, 0, 0.5), 0 0 1px rgba(255, 255, 255, 0.1)',
    radius: { sm: '8px', md: '12px', lg: '16px', xl: '20px', full: '9999px' },
    transition: { fast: '0.15s ease', normal: '0.2s ease', slow: '0.3s ease' },
    ...preset,
  };
};

// Initialize theme from saved settings - mutable singleton
let THEME = buildTheme(getSavedThemeMode(), getSavedAccentColor());

// Update THEME in place (preserves reference for components that import it)
export const updateTheme = (mode, accentColor) => {
  const newTheme = buildTheme(mode, accentColor);
  Object.keys(THEME).forEach(key => delete THEME[key]);
  Object.assign(THEME, newTheme);
  return THEME;
};

export { THEME };

// Status utilities
export const STATUS_OPTIONS = [
  { value: 'played', label: 'Played', color: '#10b981' },
  { value: 'wtp', label: 'Want to Play', color: '#f59e0b' },
  { value: 'shelved', label: 'Shelved', color: '#8b5cf6' },
  { value: 'wtf', label: "What's This?", color: '#3b82f6' },
  { value: 'never', label: 'Never Again', color: '#ef4444' },
  { value: 'unplayed', label: 'Unplayed', color: '#64748b' },
];

export const getStatusColor = (status) => STATUS_OPTIONS.find(s => s.value === status)?.color || THEME.textMuted;
export const getStatusLabel = (status) => STATUS_OPTIONS.find(s => s.value === status)?.label || status;
